import Graph from "../graph";
import Legend from "components/legend";
import { legendColor } from "components/legend";

import { getQuestionsBySubject } from "api/legacy_performance";
import { subjects as subjectFixtures } from "../fixtures";
import clsx from "clsx";

import { useState, useEffect } from "react";
import { useUser } from "hooks/user";
import { useMounted } from "hooks/react";

interface Subject {
  name: string;
  count: number;
  result: number;
}

interface SubjectsProps {
  subjects?: Subject[];
}

export default function Subjects(props: SubjectsProps): JSX.Element {
  const [subjects, setSubjects] = useState(
    props.subjects ||
      subjectFixtures
        .map((subject) => subjectInfo(subject))
        .sort((x, y) => y.count - x.count)
  );
  const [loading, setLoading] = useState(true);
  const { user } = useUser();

  const mounted = useMounted();

  useEffect(() => {
    if (!mounted) return;
    if (!user) return setLoading(false);

    fetchData();
  }, [mounted, user]);

  function fetchData() {
    if (props.subjects) return setLoading(false);

    getQuestionsBySubject().then((response) => {
      const newSubjects = response.body.map((subject) => subjectInfo(subject));
      setSubjects(newSubjects.sort((x, y) => y.count - x.count));
      setLoading(false);
    });
  }

  function totalAnswers() {
    return subjects.reduce((total, obj) => total + obj.count, 0);
  }

  function subjectInfo(subject) {
    return {
      name: subject.name,
      count: subject.answered,
      result: subjectResult(subject)
    };
  }

  function subjectResult(subject) {
    if (subject.answered == 0) return 0;

    return subject.correct / subject.answered;
  }

  function normalizedName(subject) {
    if (!subject.name) return "";

    return subject.name
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
  }

  return (
    <div>
      <Graph
        title="Respostas por matéria"
        noPadding={true}
        loading={loading}
        className={clsx({ "filter grayscale": !user })}
      >
        <header className="px-6 py-4 border-b border-b-gray-100">
          <span className="text-4xl font-semibold">{totalAnswers()}</span>{" "}
          <span className="text-lg">respostas</span>
          <div className="flex mb-1 mt-5 w-full h-4 rounded-lg overflow-hidden">
            {subjects.map((subject) => {
              return (
                <div
                  key={subject.name}
                  className="h-full"
                  style={{
                    width: `${(subject.count / totalAnswers()) * 100}%`
                  }}
                >
                  <div
                    className="w-full h-full"
                    style={{
                      backgroundColor: legendColor(normalizedName(subject))
                    }}
                  ></div>
                </div>
              );
            })}
          </div>
        </header>

        <ul>
          {subjects.map((subject) => {
            return (
              <Subject
                key={subject.name}
                {...subject}
                normalizedName={normalizedName(subject)}
              />
            );
          })}
        </ul>
      </Graph>
    </div>
  );
}

function Subject(props) {
  const { name, normalizedName, result, count } = props;

  const percentageResult = Math.round(result * 100);

  function questionsInflection(count) {
    if (count == 1) return "1 questão";

    return `${count} questões`;
  }

  if (!name) return <></>;

  return (
    <div
      className={clsx(
        "subject flex items-center justify-between px-6 py-4 border-b border-b-gray-100",
        normalizedName
      )}
    >
      <div className="flex flex-grow items-center mr-4">
        <Legend subjectSlug={normalizedName} />
        <span className="name flex-shrink-0 w-28 font-semibold md:w-36">
          {name}
        </span>
        <div className="hidden flex-grow md:block">
          <div
            className="h-2 bg-gray-300 rounded-r-lg"
            style={{ width: `${percentageResult}%` }}
          ></div>
        </div>
      </div>
      <div className="flex flex-col items-end md:flex-row md:items-center">
        <span className="result">
          <span className="font-semibold">{percentageResult}% de acertos</span>{" "}
          <div className="hidden md:inline">em média</div>
        </span>
        <span className="questions-count w-32 text-right text-emphasis-medium md:w-40">
          {questionsInflection(count)}
        </span>
      </div>
    </div>
  );
}
